import { Refresh } from "@mui/icons-material";
import {
  Autocomplete,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import clsx from "clsx";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IDropdownItem,
  IPriceRateSearchCurrentRateInfo,
  IPriceRateSearchMatchedSummary,
  IPriceRateSearchRideContext,
  PriceRateSearchExplainMode,
  SINGLE_KEY,
} from "@ryde/shared";

import { RichLogsCollapsibleFilterPanel } from "../rich-logs-components";
import {
  PriceRateSearchSupplierAutocompleteOption,
  PriceRateSearchVehicleTypeAutocompleteOption,
} from "./price-rate-search-autocomplete-options";
import {
  PriceRateSearchCurrentRateBlock,
  PriceRateSearchWinnerRateBlock,
} from "./price-rate-search-current-rate";
import {
  PriceRateSearchLockLabel,
  PriceRateSearchLogsHeaderStyled,
} from "./price-rate-search-logs.styled";
import {
  formatPriceRateSearchSupplierMetaLine,
  formatPriceRateSearchVehicleTypeMetaLine,
  formatPriceRateSearchVehicleTypePlacesLine,
  resolvePriceRateSearchDropdownItemLabel,
  resolvePriceRateSearchResetTargets,
  resolvePriceRateSearchVehicleTypeMaxPlaces,
} from "./price-rate-search-logs.utils";

export interface IPriceRateSearchLogsToolbarProps {
  explainMode: PriceRateSearchExplainMode;
  onExplainModeChange: (mode: PriceRateSearchExplainMode) => void;
  supplierId: number | null;
  vehicleTypeId: number | null;
  lockSupplier: boolean;
  lockVehicleType: boolean;
  lockCurrentPriceRate: boolean;
  onSupplierIdChange: (supplierId: number | null) => void;
  onVehicleTypeIdChange: (vehicleTypeId: number | null) => void;
  onLockSupplierChange: (locked: boolean) => void;
  onLockVehicleTypeChange: (locked: boolean) => void;
  onLockCurrentPriceRateChange: (locked: boolean) => void;
  supplierOptions: IDropdownItem[];
  vehicleTypeOptions: IDropdownItem[];
  hasSupplierReadPermission: boolean;
  showMinPrice: boolean;
  currentPriceRate: IPriceRateSearchCurrentRateInfo | null;
  matchedPriceRate: IPriceRateSearchMatchedSummary | null;
  matchedPriceRateId: number | null;
  isSearchSettled: boolean;
  onRefreshLogsClick: () => void;
  isRefreshLogsDisabled: boolean;
  rideSearchContext: IPriceRateSearchRideContext | null;
  onResetToCurrent: () => void;
}

const toOptionId = (value: IDropdownItem["value"]): number | null => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const PriceRateSearchLogsToolbar: FC<
  IPriceRateSearchLogsToolbarProps
> = ({
  explainMode,
  onExplainModeChange,
  supplierId,
  vehicleTypeId,
  lockSupplier,
  lockVehicleType,
  lockCurrentPriceRate,
  onSupplierIdChange,
  onVehicleTypeIdChange,
  onLockSupplierChange,
  onLockVehicleTypeChange,
  onLockCurrentPriceRateChange,
  supplierOptions,
  vehicleTypeOptions,
  hasSupplierReadPermission,
  showMinPrice,
  currentPriceRate,
  matchedPriceRate,
  matchedPriceRateId,
  isSearchSettled,
  onRefreshLogsClick,
  isRefreshLogsDisabled,
  rideSearchContext,
  onResetToCurrent,
}) => {
  const { t } = useTranslation();
  const [priceRateFiltersCollapsed, setPriceRateFiltersCollapsed] =
    useState(true);
  const showRideParamFilters =
    explainMode !== PriceRateSearchExplainMode.Produce;
  const supplierIds = Array.from(
    new Set(
      [
        ...supplierOptions.map(item => toOptionId(item.value)),
        supplierId,
      ].filter((id): id is number => id !== null),
    ),
  );
  const vehicleTypeMaxPlaces = resolvePriceRateSearchVehicleTypeMaxPlaces({
    vehicleTypeId,
    vehicleTypeOptions,
    rideContext: rideSearchContext,
  });
  const vehicleTypePlacesLine = formatPriceRateSearchVehicleTypePlacesLine({
    vehicleTypeMaxPlaces,
    geoProdMaxPlaces: rideSearchContext?.geoProdMaxPlaces ?? null,
    geoProdMaxPlacesB: rideSearchContext?.geoProdMaxPlacesB ?? null,
    rideDirection: rideSearchContext?.direction ?? null,
  });
  const vehicleTypeIds = Array.from(
    new Set(
      [
        ...vehicleTypeOptions.map(item => toOptionId(item.value)),
        vehicleTypeId,
      ].filter((id): id is number => id !== null),
    ),
  );
  const supplierIdLabel = t("ride.priceRateSearchEntityId", {
    defaultValue: "ID",
  });
  const supplierPriorityLabel = t("ride.priceRateSearchSupplierPriority", {
    defaultValue: "Priority",
  });
  const supplierMetaLine =
    lockSupplier && supplierId !== null
      ? formatPriceRateSearchSupplierMetaLine({
          supplierId,
          supplierOptions,
          idLabel: supplierIdLabel,
          priorityLabel: supplierPriorityLabel,
        })
      : null;
  const vehicleTypeMetaLine = lockVehicleType
    ? formatPriceRateSearchVehicleTypeMetaLine({
        vehicleTypeId,
        idLabel: supplierIdLabel,
        placesLine: vehicleTypePlacesLine,
      })
    : null;
  const ratesBlockProps = {
    currentPriceRate,
    isSearchSettled,
    matchedPriceRate,
    matchedPriceRateId,
    showPrices: showMinPrice,
  };
  const resetTargets = resolvePriceRateSearchResetTargets({
    currentPriceRate,
    rideSupplierId: rideSearchContext?.supplierId,
    rideVehicleTypeId: rideSearchContext?.vehicleTypeId,
  });
  const supplierNeedsReset =
    resetTargets.supplierId !== null &&
    (!lockSupplier || supplierId !== resetTargets.supplierId);
  const vehicleTypeNeedsReset =
    resetTargets.vehicleTypeId !== null &&
    (!lockVehicleType || vehicleTypeId !== resetTargets.vehicleTypeId);

  return (
    <PriceRateSearchLogsHeaderStyled>
      <div className="price-rate-search-logs-header__top">
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          className="price-rate-search-logs-header__modes"
          value={explainMode}
          onChange={(_, nextMode: PriceRateSearchExplainMode | null) => {
            if (nextMode) {
              onExplainModeChange(nextMode);
            }
          }}
          aria-label={t("ride.priceRateSearchExplainMode", {
            defaultValue: "Price rate search mode",
          })}
        >
          <ToggleButton value={PriceRateSearchExplainMode.Save}>
            {t("ride.priceRateSearchLockedMode", { defaultValue: "Locked" })}
          </ToggleButton>
          <ToggleButton value={PriceRateSearchExplainMode.Produce}>
            {t("ride.priceRateSearchProduceMode", {
              defaultValue: "Produce mode",
            })}
          </ToggleButton>
        </ToggleButtonGroup>
        <div className="price-rate-search-logs-header__actions">
          {showRideParamFilters &&
          Boolean(currentPriceRate) &&
          (supplierNeedsReset || vehicleTypeNeedsReset) ? (
            <button
              type="button"
              className="log__filters-refresh-btn price-rate-search-logs-header__reset-btn"
              onClick={onResetToCurrent}
            >
              {t("ride.priceRateSearchResetToCurrent")}
            </button>
          ) : null}
          <button
            type="button"
            className="log__filters-refresh-btn price-rate-search-logs-header__refresh-btn"
            onClick={onRefreshLogsClick}
            disabled={isRefreshLogsDisabled}
            title={t("general.REFRESH")}
            aria-label={t("general.REFRESH")}
          >
            <Refresh fontSize="small" />
          </button>
        </div>
      </div>
      {showRideParamFilters ? (
        <RichLogsCollapsibleFilterPanel
          className="price-rate-search-logs-header__filters-panel"
          collapsed={priceRateFiltersCollapsed}
          collapseLabel={t("ride.priceRateSearchCollapsePriceRateFilters", {
            defaultValue: "Hide price rate filters",
          })}
          contentClassName="price-rate-search-logs-header__filters-panel-content"
          emoji="🏷️"
          expandLabel={t("ride.priceRateSearchExpandPriceRateFilters", {
            defaultValue: "Show price rate filters",
          })}
          onToggle={() => setPriceRateFiltersCollapsed(value => !value)}
          title={t("ride.priceRateSearchPriceRateFilters", {
            defaultValue: "Price rate filters",
          })}
        >
          <PriceRateSearchCurrentRateBlock {...ratesBlockProps} />
          <div className="price-rate-search-logs-header__filters">
            {hasSupplierReadPermission && (
              <div
                className={clsx(
                  "price-rate-search-logs-header__lock-row",
                  lockSupplier && "price-rate-search-logs-header__lock-row--on",
                )}
              >
                <PriceRateSearchLockLabel
                  control={
                    <Switch
                      size="small"
                      checked={lockSupplier}
                      onChange={(_, checked) => onLockSupplierChange(checked)}
                    />
                  }
                  label={t("ride.priceRateSearchLockSupplier", {
                    defaultValue: "Lock supplier",
                  })}
                />
                <div className="price-rate-search-logs-header__supplier-field">
                  <Autocomplete
                    disableClearable={lockSupplier}
                    disabled={!lockSupplier}
                    getOptionLabel={id =>
                      resolvePriceRateSearchDropdownItemLabel(
                        supplierOptions,
                        id ?? null,
                      )
                    }
                    isOptionEqualToValue={(option, selected) =>
                      option === selected
                    }
                    onChange={(_, nextId) => onSupplierIdChange(nextId)}
                    options={supplierIds}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={t(`supplier.${SINGLE_KEY}`)}
                      />
                    )}
                    renderOption={(liProps, id) => (
                      <PriceRateSearchSupplierAutocompleteOption
                        id={id}
                        idLabel={supplierIdLabel}
                        liProps={liProps}
                        options={supplierOptions}
                        priorityLabel={supplierPriorityLabel}
                      />
                    )}
                    size="small"
                    value={supplierId}
                  />
                  {supplierMetaLine ? (
                    <span className="price-rate-search-logs-header__field-meta">
                      {supplierMetaLine}
                    </span>
                  ) : null}
                </div>
              </div>
            )}
            <div
              className={clsx(
                "price-rate-search-logs-header__lock-row",
                lockVehicleType &&
                  "price-rate-search-logs-header__lock-row--on",
              )}
            >
              <PriceRateSearchLockLabel
                control={
                  <Switch
                    size="small"
                    checked={lockVehicleType}
                    onChange={(_, checked) => onLockVehicleTypeChange(checked)}
                  />
                }
                label={t("ride.priceRateSearchLockVehicleType", {
                  defaultValue: "Lock vehicle type",
                })}
              />
              <div className="price-rate-search-logs-header__vehicle-type-field">
                <Autocomplete
                  disableClearable={lockVehicleType}
                  disabled={!lockVehicleType}
                  getOptionLabel={id =>
                    resolvePriceRateSearchDropdownItemLabel(
                      vehicleTypeOptions,
                      id ?? null,
                    )
                  }
                  isOptionEqualToValue={(option, selected) =>
                    option === selected
                  }
                  onChange={(_, nextId) => onVehicleTypeIdChange(nextId)}
                  options={vehicleTypeIds}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t(`vehicleType.${SINGLE_KEY}`)}
                    />
                  )}
                  renderOption={(liProps, id) => (
                    <PriceRateSearchVehicleTypeAutocompleteOption
                      id={id}
                      idLabel={supplierIdLabel}
                      liProps={liProps}
                      options={vehicleTypeOptions}
                    />
                  )}
                  size="small"
                  value={vehicleTypeId}
                />
                {vehicleTypeMetaLine ? (
                  <span className="price-rate-search-logs-header__field-meta price-rate-search-logs-header__field-meta--wrap">
                    {vehicleTypeMetaLine}
                  </span>
                ) : null}
              </div>
            </div>
            <div
              className={clsx(
                "price-rate-search-logs-header__lock-row",
                lockCurrentPriceRate &&
                  Boolean(currentPriceRate) &&
                  "price-rate-search-logs-header__lock-row--on",
                Boolean(currentPriceRate) &&
                  "price-rate-search-logs-header__lock-row--switch-only",
              )}
            >
              <PriceRateSearchLockLabel
                control={
                  <Switch
                    size="small"
                    checked={lockCurrentPriceRate && Boolean(currentPriceRate)}
                    disabled={!currentPriceRate}
                    onChange={(_, checked) =>
                      onLockCurrentPriceRateChange(checked)
                    }
                  />
                }
                label={t("ride.priceRateSearchLockExisting")}
              />
              {!currentPriceRate ? (
                <span className="price-rate-search-logs-header__lock-hint">
                  {t("ride.priceRateSearchNoCurrentRate")}
                </span>
              ) : null}
            </div>
          </div>
        </RichLogsCollapsibleFilterPanel>
      ) : null}
      <PriceRateSearchWinnerRateBlock {...ratesBlockProps} />
    </PriceRateSearchLogsHeaderStyled>
  );
};
